// =============================================================================
// MeuDriver — Hook: useFaq (Batch T1)
// FAQ e Base de Conhecimento — leitura pública + CRUD admin
// =============================================================================

import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface FaqCategory {
  id: string
  name: string
  display_order: number
  target_audience: 'all' | 'students' | 'instructors'
}

export interface FaqArticle {
  id: string
  category_id: string
  question: string
  answer: string
  display_order: number
  is_published: boolean
  created_at: string
  updated_at: string
}

export interface FaqArticleInsert {
  category_id: string
  question: string
  answer: string
  display_order?: number
  is_published?: boolean
}

export interface FaqArticleUpdate {
  category_id?: string
  question?: string
  answer?: string
  display_order?: number
  is_published?: boolean
}

// ─── Query keys ───────────────────────────────────────────────────────────────

const faqKeys = {
  categories: ['faq-categories'] as const,
  articles:   (categoryId?: string) => ['faq-articles', categoryId ?? 'all'] as const,
}

// ─── useFaqCategories ─────────────────────────────────────────────────────────

export function useFaqCategories() {
  return useQuery<FaqCategory[]>({
    queryKey: faqKeys.categories,
    staleTime: 1000 * 60 * 10,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('faq_categories')
        .select('*')
        .order('display_order', { ascending: true })

      if (error) throw new Error(error.message)
      return (data ?? []) as FaqCategory[]
    },
  })
}

// ─── useFaqArticles ───────────────────────────────────────────────────────────
// Retorna artigos publicados, filtrados por categoria se categoryId fornecido.

export function useFaqArticles(categoryId?: string) {
  return useQuery<FaqArticle[]>({
    queryKey: faqKeys.articles(categoryId),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      let query = supabase
        .from('faq_articles')
        .select('*')
        .eq('is_published', true)
        .order('display_order', { ascending: true })

      if (categoryId) {
        query = query.eq('category_id', categoryId)
      }

      const { data, error } = await query
      if (error) throw new Error(error.message)
      return (data ?? []) as FaqArticle[]
    },
  })
}

// ─── useSearchFaq ─────────────────────────────────────────────────────────────
// Filtragem client-side sobre todos os artigos publicados.

export function useSearchFaq(query: string) {
  const { data: allArticles = [], isLoading } = useFaqArticles()

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return allArticles
    return allArticles.filter(
      (a) =>
        a.question.toLowerCase().includes(q) ||
        a.answer.toLowerCase().includes(q),
    )
  }, [allArticles, query])

  return { data: results, isLoading }
}

// ─── useAdminFaq ──────────────────────────────────────────────────────────────
// Admin: busca todos os artigos (incluindo não publicados) de todas categorias.

export function useAdminFaq() {
  return useQuery<FaqArticle[]>({
    queryKey: ['faq-admin-articles'],
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('faq_articles')
        .select('*')
        .order('category_id', { ascending: true })
        .order('display_order', { ascending: true })

      if (error) throw new Error(error.message)
      return (data ?? []) as FaqArticle[]
    },
  })
}

// ─── useCreateFaqArticle ──────────────────────────────────────────────────────

export function useCreateFaqArticle() {
  const qc = useQueryClient()

  return useMutation<FaqArticle, Error, FaqArticleInsert>({
    mutationFn: async (payload) => {
      const { data, error } = await supabase
        .from('faq_articles')
        .insert(payload)
        .select()
        .single()

      if (error) throw new Error(error.message)
      return data as FaqArticle
    },
    onSuccess: () => {
      toast.success('Artigo criado com sucesso')
      qc.invalidateQueries({ queryKey: ['faq-admin-articles'] })
      qc.invalidateQueries({ queryKey: faqKeys.articles() })
    },
    onError: (err) => {
      toast.error(`Erro ao criar artigo: ${err.message}`)
    },
  })
}

// ─── useUpdateFaqArticle ──────────────────────────────────────────────────────

export function useUpdateFaqArticle() {
  const qc = useQueryClient()

  return useMutation<void, Error, { id: string; updates: FaqArticleUpdate }>({
    mutationFn: async ({ id, updates }) => {
      const { error } = await supabase
        .from('faq_articles')
        .update(updates)
        .eq('id', id)

      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      toast.success('Artigo atualizado')
      qc.invalidateQueries({ queryKey: ['faq-admin-articles'] })
      qc.invalidateQueries({ queryKey: faqKeys.articles() })
    },
    onError: (err) => {
      toast.error(`Erro ao atualizar artigo: ${err.message}`)
    },
  })
}

// ─── useDeleteFaqArticle ──────────────────────────────────────────────────────

export function useDeleteFaqArticle() {
  const qc = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('faq_articles')
        .delete()
        .eq('id', id)

      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      toast.success('Artigo removido')
      qc.invalidateQueries({ queryKey: ['faq-admin-articles'] })
      qc.invalidateQueries({ queryKey: faqKeys.articles() })
    },
    onError: (err) => {
      toast.error(`Erro ao remover artigo: ${err.message}`)
    },
  })
}
